import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Eye,
  MessageSquare,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SMEPendingValidations = () => {
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInterventions();
  }, []);

  const fetchInterventions = async () => {
    try {
      const response = await axios.get(`${API}/interventions`);
      setInterventions(response.data.filter(i => i.status === 'pending'));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="pending-validations-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Validations en attente</h1>
          <p className="text-slate-500 mt-1">Examinez et validez les interventions des consultants</p>
        </div>

        {/* Interventions Table */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {interventions.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <p className="text-slate-500">Tout est à jour ! Aucune validation en attente.</p>
                <Button asChild variant="outline" className="mt-4">
                  <Link to="/pme/history">Voir l'historique</Link>
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-slate-500 font-medium">Type</TableHead>
                    <TableHead className="text-slate-500 font-medium">Consultant</TableHead>
                    <TableHead className="text-slate-500 font-medium">Date</TableHead>
                    <TableHead className="text-slate-500 font-medium">Durée</TableHead>
                    <TableHead className="text-slate-500 font-medium">Statut</TableHead>
                    <TableHead className="text-slate-500 font-medium text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {interventions.map((intervention) => (
                    <TableRow key={intervention.id} data-testid={`row-${intervention.id}`}>
                      <TableCell className="font-medium text-slate-900">
                        {intervention.intervention_type}
                      </TableCell>
                      <TableCell className="text-slate-600">{intervention.consultant_name}</TableCell>
                      <TableCell className="text-slate-600">{intervention.date}</TableCell>
                      <TableCell className="text-slate-600">{intervention.duration_hours}h</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-200">
                          <Clock className="w-3 h-3 mr-1" />
                          En attente
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/pme/intervention/${intervention.id}`)}
                            data-testid={`view-${intervention.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm"
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            onClick={() => navigate(`/pme/feedback/${intervention.id}`)}
                            data-testid={`feedback-${intervention.id}`}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Valider
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
